import { MEDIA_TYPE_3D_MODEL } from './model-3d.js';

export const PRODUCT_MEDIA_3D_BUTTON_CLICKED_EVENT_NAME = '3d_button_clicked';

export const PRODUCT_MEDIA_3D_VIEWER_LOADED_EVENT_NAME = '3d_viewer_loaded';

export const PRODUCT_MEDIA_3D_MODEL_INTERACTED_EVENT_NAME = '3d_model_interacted';

export const PRODUCT_MEDIA_3D_VIEWER_LOAD_FAILED_EVENT_NAME = '3d_viewer_load_failed';

export const PRODUCT_MEDIA_3D_VIEWER_LOAD_FAILED_ERROR_REASON = 'model_load_failed';

/**
 * Shared detail shape for typed 3D model metrics (button click + viewer events).
 *
 * @param {unknown} detail
 * @returns {detail is {
 *   modelUrl: string,
 *   mediaType: string,
 *   skuStripped?: string,
 *   catalog?: string,
 * }}
 */
export function isValidProductMediaLaunch3dDetail(detail) {
	if (detail === null || typeof detail !== 'object') {
		return false;
	}

	/** @type {Record<string, unknown>} */
	const record = detail;
	const modelUrl = record.modelUrl;
	const mediaType = record.mediaType;

	return mediaType === MEDIA_TYPE_3D_MODEL
		&& typeof modelUrl === 'string'
		&& modelUrl.trim() !== '';
}

/**
 * @returns {boolean}
 */
function canTrackProductMedia3dLaunch() {
	return Boolean(globalThis.tracking?.google?.use_gtag_admind_analytics)
		&& typeof globalThis.rp_app?.track?.track_ec_general === 'function';
}

/**
 * @param {{
 *   catalog?: string,
 *   errorReason?: string,
 *   mediaType?: string,
 *   skuStripped?: string,
 * }} [detail]
 * @returns {Record<string, string>}
 */
function buildProductMedia3dAnalyticsPayload(detail = {}) {
	/** @type {Record<string, string>} */
	const payload = {};

	if (typeof detail.catalog === 'string' && detail.catalog !== '') {
		payload.catalog = detail.catalog;
	}

	if (typeof detail.mediaType === 'string' && detail.mediaType !== '') {
		payload.media_type = detail.mediaType;
	}

	if (typeof detail.skuStripped === 'string' && detail.skuStripped !== '') {
		payload.sku_stripped = detail.skuStripped;
	}

	if (typeof detail.errorReason === 'string' && detail.errorReason !== '') {
		payload.error_reason = detail.errorReason;
	}

	return payload;
}

/**
 * @param {string} eventName
 * @param {{
 *   catalog?: string,
 *   errorReason?: string,
 *   mediaType?: string,
 *   skuStripped?: string,
 * }} [detail]
 */
function trackProductMedia3dEvent(eventName, detail = {}) {
	if (!canTrackProductMedia3dLaunch()) {
		return;
	}

	try {
		globalThis.rp_app.track.track_ec_general(eventName, buildProductMedia3dAnalyticsPayload(detail));
	} catch (_error) {
	}
}

/**
 * Fires `3d_button_clicked` when the shopper selects a typed `3d_model` thumbnail.
 *
 * @param {{
 *   catalog?: string,
 *   mediaType?: string,
 *   skuStripped?: string,
 * }} [detail]
 */
export function trackProductMedia3dButtonClicked(detail = {}) {
	trackProductMedia3dEvent(PRODUCT_MEDIA_3D_BUTTON_CLICKED_EVENT_NAME, detail);
}

/**
 * Fires `3d_viewer_loaded` when the interactive model finishes loading.
 *
 * @param {{
 *   catalog?: string,
 *   mediaType?: string,
 *   skuStripped?: string,
 * }} [detail]
 */
export function trackProductMedia3dViewerLoaded(detail = {}) {
	trackProductMedia3dEvent(PRODUCT_MEDIA_3D_VIEWER_LOADED_EVENT_NAME, detail);
}

/**
 * Fires `3d_model_interacted` when the shopper rotates or zooms the model.
 *
 * @param {{
 *   catalog?: string,
 *   mediaType?: string,
 *   skuStripped?: string,
 * }} [detail]
 */
export function trackProductMedia3dModelInteracted(detail = {}) {
	trackProductMedia3dEvent(PRODUCT_MEDIA_3D_MODEL_INTERACTED_EVENT_NAME, detail);
}

/**
 * Fires `3d_viewer_load_failed` when the model fails to load.
 *
 * @param {{
 *   catalog?: string,
 *   errorReason?: string,
 *   mediaType?: string,
 *   skuStripped?: string,
 * }} [detail]
 */
export function trackProductMedia3dViewerLoadFailed(detail = {}) {
	trackProductMedia3dEvent(PRODUCT_MEDIA_3D_VIEWER_LOAD_FAILED_EVENT_NAME, {
		...detail,
		errorReason: typeof detail.errorReason === 'string' && detail.errorReason !== ''
			? detail.errorReason
			: PRODUCT_MEDIA_3D_VIEWER_LOAD_FAILED_ERROR_REASON,
	});
}
